import { Box, Paper } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

const HighlightedPolicy: React.FC<{
  policyText: string;
  conflictingText: string[];
}> = ({ policyText, conflictingText }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstHighlightRef = useRef<HTMLDivElement>(null);

  const lines = policyText.split('\n');
  const flatConflicts = conflictingText.flatMap(ct =>
    ct
      .split('\n')
      .map(l => l.trim().toLowerCase())
      .filter(Boolean)
  );
  const firstConflictIndex = flatConflicts.length
    ? lines.findIndex(line => flatConflicts.some(ct => line.toLowerCase().includes(ct)))
    : -1;

  useEffect(() => {
    if (!firstHighlightRef.current || !scrollRef.current) return;
    const id = window.requestAnimationFrame(() => {
      const container = scrollRef.current;
      const target = firstHighlightRef.current;
      if (!container || !target) return;
      const offset =
        target.offsetTop -
        container.offsetTop -
        container.clientHeight / 2 +
        target.clientHeight / 2;
      container.scrollTop = Math.max(0, offset);
    });
    return () => window.cancelAnimationFrame(id);
  }, [policyText, conflictingText]);

  return (
    <Paper
      ref={scrollRef}
      variant="outlined"
      className="markdown-container"
      sx={{
        bgcolor: '#fcfcfd',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        p: 1.5,
      }}
    >
      {lines.map((line, i) => {
        const hasConflict = flatConflicts.some(ct => line.toLowerCase().includes(ct));
        return (
          <Box
            key={i}
            ref={i === firstConflictIndex ? firstHighlightRef : undefined}
            sx={{
              px: 1,
              py: 0.25,
              bgcolor: hasConflict ? 'rgba(255, 235, 59, 0.3)' : 'transparent',
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{line}</ReactMarkdown>
          </Box>
        );
      })}
    </Paper>
  );
};

export default HighlightedPolicy;
